import { GateUsageProcessType } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import ButtonSwitchMulti, {
  ButtonSwitchMultiOption,
} from '../../components/actions/ButtonSwitchMulti';
import InputNumber from '../../components/inputs/InputNumber';
import InputSlider from '../../components/inputs/InputSlider';
import { Container } from '../../components/layout/ContainerFlex';
import SectionOptional from '../../components/layout/SectionOptional';
import LoadingIndicator from '../../components/LoadingIndicator';
import SidebarContainer, {
  SidebarContainerProps,
} from '../../containers/SidebarContainer';
import { getAgentEditorSettings } from '../../resourcePolicy/AgentEditorSettings';
import { warehouseSelected } from '../../store/warehouse.state';
import {
  analyzeGateUsageJobDurationSettings,
  analyzeGateUsageScheduleDockingDuration,
  analyzeGateUsageScheduleForward,
  analyzeGateUsageScheduleUnDockingDuration,
  analyzeGateUsageScheduleWaitingCost,
} from '../store/analyze.state';
import ThresholdControls from './ThresholdControls';

type GateUsageJobSettingProps = {
  title: string;
  process: GateUsageProcessType;
};

const GateUsageJobSetting: React.FC<GateUsageJobSettingProps> = props => {
  const [jobSettings, setJobSettings] = useRecoilState(
    analyzeGateUsageJobDurationSettings,
  );

  const others = _.filter(jobSettings, j => j.process !== props.process);
  const current = _.find(jobSettings, j => j.process === props.process);

  return (
    <SectionOptional
      id={`gate-usage-job-settings-${props.process}`}
      title={props.title}
      value={!_.isNil(current)}
      onChange={enabled =>
        enabled
          ? setJobSettings([
              ...others,
              {
                process: props.process,
                agentCount: 1,
              },
            ])
          : setJobSettings(others)
      }
    >
      <InputNumber
        value={current?.agentCount}
        onChange={v =>
          setJobSettings([
            ...others,
            {
              process: props.process,
              agentCount: v,
            },
          ])
        }
      />
    </SectionOptional>
  );
};

const SimulationScheduleControlsSidebar: React.FC = () => {
  const { t } = useTranslation('simulation');
  const wh = useRecoilValue(warehouseSelected);
  const [waitingCost, setWaitingCost] = useRecoilState(
    analyzeGateUsageScheduleWaitingCost,
  );
  const [dockingDuration, setDockingDuration] = useRecoilState(
    analyzeGateUsageScheduleDockingDuration,
  );
  const [undockingDuration, setUndockingDuration] = useRecoilState(
    analyzeGateUsageScheduleUnDockingDuration,
  );
  const [isForward, setIsForward] = useRecoilState(
    analyzeGateUsageScheduleForward,
  );

  const directionOptions: ButtonSwitchMultiOption[] = [
    { label: t`Outbound` },
    { label: t`Inbound` },
  ];
  const selectedDirectionOptions = isForward ? 1 : 0;

  const editorSettings = getAgentEditorSettings(
    t,
    wh.measurementSystem,
    wh.currency,
  );

  const costSettings = editorSettings.hourCost;

  return (
    <Container col hasOverflowY className="space-y-4 p-4">
      <div className="space-y-2">
        <ButtonSwitchMulti
          title={t`Schedule Mode`}
          buttonType="switchon"
          autoSize
          className="text-xs"
          selectedIndex={selectedDirectionOptions}
          onClick={i => setIsForward(!!i)}
          options={directionOptions}
        />
      </div>

      <div className="space-y-4">
        <InputSlider
          title={t`Idle time cost`}
          min={costSettings.min}
          max={costSettings.max}
          values={[costSettings.load(waitingCost)]}
          stepSize={costSettings.step}
          unitOfMeasure={costSettings.unitOfMeasure}
          onChange={v => setWaitingCost(costSettings.save(v[0]))}
          segmentsClassNames={[
            'bg-menu-button',
            'bg-menu-active',
            'bg-menu-button',
          ]}
        />

        <InputSlider
          title={t`Docking Gap Before`}
          min={0}
          max={500}
          values={[dockingDuration]}
          stepSize={1}
          unitOfMeasure={t`sec`}
          onChange={v => setDockingDuration(v[0])}
          segmentsClassNames={[
            'bg-menu-button',
            'bg-menu-active',
            'bg-menu-button',
          ]}
        />

        <InputSlider
          title={t`Undocking Gap After`}
          min={0}
          max={500}
          values={[undockingDuration]}
          stepSize={1}
          unitOfMeasure={t`sec`}
          onChange={v => setUndockingDuration(v[0])}
          segmentsClassNames={[
            'bg-menu-button',
            'bg-menu-active',
            'bg-menu-button',
          ]}
        />
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">{t`Job Settings`}</h3>
        <GateUsageJobSetting
          process={GateUsageProcessType.HANDLING}
          title={t`Handling`}
        />
        <GateUsageJobSetting
          process={GateUsageProcessType.LOADING}
          title={t`Loading`}
        />
      </div>
      <ThresholdControls />
    </Container>
  );
};

// export default ;

export default () => {
  const { t } = useTranslation('simulation');

  const containerProps: SidebarContainerProps = {
    type: 'sidebar-simulation-schedule-docks-controls',
    title: t`Schedule Controls`,
    size: 'md',
    sideRight: true,
  };

  return (
    <SidebarContainer {...containerProps}>
      <Suspense
        fallback={<LoadingIndicator message={t`Loading Schedule Controls`} />}
      >
        <SimulationScheduleControlsSidebar />
      </Suspense>
    </SidebarContainer>
  );
};
